import { Box, IconButton, Stack, Tooltip, useTheme } from "@mui/material";
import { ChatMessageWithParts, ContentType } from "src/views/assistant/types";
import {
  ContentCopyOutlined,
  ReplayOutlined,
  ThumbDownOutlined,
  ThumbUpOutlined,
} from "@mui/icons-material";
import {
  useGetFeedbackRanking,
  useUpdateChatRanking,
} from "../../../views/assistant/AssistantServices";

import { GOOD_OR_NOT_GOOD_FEEDBACK_TYPE } from "src/constants";
import { trackEvent } from "src/config/posthog";
import { useChatContext } from "../context/ChatContext";
import { convertMarkdownToHtml } from "src/helpers/markdownUtils";
import { formatDialogueForRanking } from "../ChatStreaming/services/chatHelpers";
type AssistantPrimaryActionsProps = {
  chatMessage: ChatMessageWithParts;
  dialogue: ChatMessageWithParts[];
  isChatConnected: boolean;
  isChatMessageStreamStopped: boolean;
  regenerateResponse: (index: string) => void;
};

const AssistantPrimaryActions = ({
  chatMessage,
  dialogue,
  isChatConnected,
  isChatMessageStreamStopped,
  regenerateResponse,
}: AssistantPrimaryActionsProps) => {
  const theme = useTheme();

  const { dialogueId } = useChatContext();

  const { mutate: updateChatRanking } = useUpdateChatRanking();
  const { data: feedbackRanking } = useGetFeedbackRanking(
    isChatMessageStreamStopped && dialogueId,
  );

  const isGoodOrNotGoodFeedbackType =
    chatMessage.ranking_type === GOOD_OR_NOT_GOOD_FEEDBACK_TYPE;

  const messageIndex = dialogue.findIndex((msg) => msg.id === chatMessage.id);
  const isLastAssistantMessage =
    messageIndex === dialogue.findLastIndex((msg) => msg.type === "Assistant");

  const handleRegenerate = () => {
    if (chatMessage?.id) {
      trackEvent("ai_response_regenerated", {
        dialogue_id: dialogueId,
        message_id: chatMessage.id,
      });
      regenerateResponse(chatMessage?.id);
    }
  };

  const handleCopyToClipboard = async () => {
    const messageIndex = dialogue.findIndex((msg) => msg.id === chatMessage.id);
    const dialogueText = dialogue[messageIndex].contentParts
      .filter((part) => part.type === ContentType.TEXT && part.text.trim())
      .map((part) => part.text.trim())
      .join("");
    const htmlDialogueText = await convertMarkdownToHtml(dialogueText);
    if (htmlDialogueText) {
      const data = new ClipboardItem({
        "text/plain": new Blob([dialogueText], { type: "text/plain" }),
        "text/html": new Blob([htmlDialogueText], { type: "text/html" }),
      });
      await navigator.clipboard.write([data]);
    }
  };
  const handleThumbsUp = () => {
    const formattedDialogue = formatDialogueForRanking(dialogue);

    trackEvent("ai_response_rated", {
      rating: "up",
      dialogue_id: dialogueId,
      message_id: chatMessage?.id,
    });

    //@ts-ignore
    updateChatRanking({
      dialogue_id: dialogueId,
      context_dialogue: formattedDialogue,
      ranking_update: [{ message_id: chatMessage?.id, ranking: 1 }],
    });
  };

  const handleThumbsDown = () => {
    const formattedDialogue = formatDialogueForRanking(dialogue);

    trackEvent("ai_response_rated", {
      rating: "down",
      dialogue_id: dialogueId,
      message_id: chatMessage?.id,
    });

    //@ts-ignore
    updateChatRanking({
      dialogue_id: dialogueId,
      context_dialogue: formattedDialogue,
      ranking_update: [{ message_id: chatMessage?.id, ranking: 0 }],
    });
  };

  return (
    <Box
      sx={{
        p: 0.3,
      }}
    >
      <Stack direction="row" gap={0.5}>
        {!isChatConnected && (
          <>
            <Tooltip title="Copy" placement="bottom" enterDelay={0}>
              <IconButton
                onClick={handleCopyToClipboard}
                data-attr="assistant:btn:copy-message"
              >
                <ContentCopyOutlined
                  fontSize="small"
                  sx={{ color: "#454746" }}
                />
              </IconButton>
            </Tooltip>

            <Tooltip title="Regenerate" placement="bottom" enterDelay={0}>
              <IconButton
                onClick={handleRegenerate}
                data-attr="assistant:btn:regenerate-response"
              >
                <ReplayOutlined fontSize="small" sx={{ color: "#454746" }} />
              </IconButton>
            </Tooltip>
          </>
        )}
        {isLastAssistantMessage &&
          isChatMessageStreamStopped &&
          isGoodOrNotGoodFeedbackType && (
            <>
              <Tooltip title="Good response" placement="bottom" enterDelay={0}>
                <IconButton
                  onClick={handleThumbsUp}
                  data-attr="assistant:btn:thumbs-up-response"
                >
                  <ThumbUpOutlined
                    fontSize="small"
                    sx={{
                      color:
                        feedbackRanking?.feedback_value === 1
                          ? theme.palette.primary.main
                          : "#454746",
                    }}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title="Bad response" placement="bottom" enterDelay={0}>
                <IconButton
                  onClick={handleThumbsDown}
                  data-attr="assistant:btn:thumbs-down-response"
                >
                  <ThumbDownOutlined
                    fontSize="small"
                    sx={{
                      color:
                        feedbackRanking?.feedback_value === 0
                          ? theme.palette.error.main
                          : "#454746",
                    }}
                  />
                </IconButton>
              </Tooltip>
            </>
          )}
      </Stack>
    </Box>
  );
};

export default AssistantPrimaryActions;
